// Phase 85.A.158 — Modal khai điểm trình self-declare (thay cho OCR ảnh).
// Form đơn giản: điểm + chọn nền tảng + 2 cam kết. Modal OCR cũ vẫn giữ để
// admin có thể bật lại qua setting `rating_verification_mode`.
import { useEffect, useState } from "react";
import { m as motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, CheckCircle, ShieldWarning, TrendUp, Lock, MapPin } from "@phosphor-icons/react";

import { api } from "../../../lib/api";
import { authApi } from "../../../lib/authApi";
import { useVnProvinces } from "../../../hooks/useVnAdmin";
import { useAuth } from "../../../contexts/AuthContext";

const PLATFORMS = [
  { code: "Baseline", color: "#0EA5E9", desc: "ứng dụng Baseline" },
  { code: "PVNA", color: "#FF1F4F", desc: "Pickleball VN Network" },
  { code: "DUPR", color: "#A8FF00", desc: "DUPR Global" },
];

const SCORE_MIN = 2.0;
const SCORE_MAX = 7.5;

export default function SelfRatingDeclareModal({ open, onClose, onSubmitted }) {
  const [score, setScore] = useState("");
  const [platform, setPlatform] = useState(null);
  // Phase 90.C.rv-home-club — Sân sinh hoạt (bắt buộc) + tỉnh/thành (tùy chọn)
  // + ghi chú (tùy chọn).
  const [homeClub, setHomeClub] = useState("");
  const [provinceCode, setProvinceCode] = useState("");
  const [note, setNote] = useState("");
  const [confirmTruthful, setConfirmTruthful] = useState(false);
  const [acceptLock, setAcceptLock] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { provinces } = useVnProvinces();
  const { user, refreshMe } = useAuth();

  useEffect(() => {
    if (!open) {
      setScore("");
      setPlatform(null);
      setHomeClub("");
      setProvinceCode("");
      setNote("");
      setConfirmTruthful(false);
      setAcceptLock(false);
      setSubmitting(false);
    }
  }, [open]);

  if (!open) return null;

  const scoreNum = parseFloat(score);
  const scoreValid = !isNaN(scoreNum) && scoreNum >= SCORE_MIN && scoreNum <= SCORE_MAX;
  const canSubmit = scoreValid && !!platform && !!homeClub.trim() && confirmTruthful && acceptLock && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const r = await api.post("/athlete/rating-verification/self-declare", {
        declared_score: scoreNum,
        platform,
        home_club: homeClub.trim(),
        home_club_province_code: provinceCode || null,
        note: note.trim() || null,
        confirm_truthful: true,
        accept_lock_if_false: true,
      });
      toast.success("Đã gửi! BTC sẽ duyệt trong 24h.");
      // v16.60.21 — Nếu user chọn Tỉnh/Thành ở form này mà hồ sơ chưa có
      // province_code → lưu luôn vào hồ sơ để modal "Cập nhật hồ sơ" kế
      // tiếp (khi đăng ký giải) được điền sẵn, đỡ phải chọn lại.
      if (provinceCode && user && !user.province_code) {
        try {
          await authApi.updateMe({ province_code: provinceCode });
          await refreshMe();
        } catch { /* best-effort — không chặn flow */ }
      }
      onSubmitted?.(r?.data);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gửi thất bại — thử lại sau.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[420] flex items-end sm:items-center justify-center bg-black/85 backdrop-blur-md px-3 pb-3 sm:pb-0"
        data-testid="self-declare-modal-backdrop"
      >
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.96 }}
          transition={{ type: "spring", stiffness: 300, damping: 28 }}
          className="relative w-full max-w-md bg-gradient-to-br from-[#0b1322] via-[#0e1729] to-[#0b1322] rounded-3xl border border-white/12 shadow-[0_20px_80px_-20px_rgba(168,255,0,0.25)] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="relative px-5 pt-5 pb-3 border-b border-white/8">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#A8FF00]/85 font-black">
                  Xác thực điểm trình
                </div>
                <div className="mt-0.5 text-[18px] font-black text-white leading-tight">
                  Khai điểm trình hiện tại
                </div>
                <div className="mt-0.5 text-[11px] text-white/55 leading-snug">
                  Nhanh, không cần chụp ảnh. BTC duyệt thủ công trong 24h.
                </div>
              </div>
              <button
                onClick={onClose} disabled={submitting}
                className="w-8 h-8 rounded-xl bg-white/[0.06] hover:bg-white/[0.12] border border-white/15 flex items-center justify-center text-white/65 transition disabled:opacity-50"
                data-testid="self-declare-close-btn"
                aria-label="Đóng"
              ><X size={14} weight="bold" /></button>
            </div>
          </div>

          {/* Body */}
          <div className="px-5 pt-4 pb-5 space-y-4 max-h-[80dvh] overflow-y-auto">
            {/* Score input */}
            <div>
              <label className="block text-[11px] font-bold text-white/85 mb-1.5 tracking-wide">
                Điểm trình hiện tại của bạn <span className="text-rose-400">*</span>
              </label>
              <div className="relative">
                <TrendUp size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8FF00]" />
                <input
                  type="number" step="0.001" min={SCORE_MIN} max={SCORE_MAX}
                  inputMode="decimal" autoFocus
                  value={score} onChange={(e) => setScore(e.target.value)}
                  placeholder="vd: 3.500"
                  className="w-full pl-9 pr-3 h-11 rounded-xl bg-white/[0.05] border border-white/15 text-white text-[15px] font-bold placeholder:text-white/30 focus:border-[#A8FF00]/60 focus:bg-white/[0.08] outline-none transition"
                  data-testid="self-declare-score-input"
                />
              </div>
              <div className="mt-1 text-[10px] text-white/45">
                Cho phép {SCORE_MIN.toFixed(1)} – {SCORE_MAX.toFixed(1)}. Dùng dấu chấm (.) cho thập phân.
                {score && !scoreValid && (
                  <span className="ml-1.5 text-rose-300 font-bold">← Ngoài khoảng cho phép</span>
                )}
              </div>
            </div>

            {/* Platform select */}
            <div>
              <label className="block text-[11px] font-bold text-white/85 mb-1.5 tracking-wide">
                Nền tảng cung cấp điểm trình <span className="text-rose-400">*</span>
              </label>
              <div className="grid grid-cols-3 gap-2">
                {PLATFORMS.map((p) => {
                  const active = platform === p.code;
                  return (
                    <button
                      key={p.code} type="button"
                      onClick={() => setPlatform(p.code)}
                      className={`relative h-14 rounded-xl border-2 transition active:scale-95 ${
                        active
                          ? "bg-white/[0.08] shadow-[0_4px_20px_-4px_rgba(168,255,0,0.35)]"
                          : "bg-white/[0.03] border-white/12 hover:bg-white/[0.06]"
                      }`}
                      style={active ? { borderColor: p.color, boxShadow: `0 4px 20px -4px ${p.color}55` } : {}}
                      data-testid={`self-declare-platform-${p.code.toLowerCase()}`}
                    >
                      <div className="flex flex-col items-center justify-center h-full">
                        <div
                          className="text-[13px] font-black tracking-wider"
                          style={{ color: active ? p.color : "rgba(255,255,255,0.85)" }}
                        >{p.code}</div>
                        <div className="text-[8.5px] text-white/45 leading-none mt-0.5">{p.desc}</div>
                      </div>
                      {active && (
                        <CheckCircle
                          size={14} weight="fill"
                          className="absolute -top-1.5 -right-1.5 drop-shadow-md"
                          style={{ color: p.color }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Home club (Phase 90.C.rv-home-club) */}
            <div>
              <label className="block text-[11px] font-bold text-white/85 mb-1.5 tracking-wide">
                Tên sân Pickleball bạn thường sinh hoạt? <span className="text-rose-400">*</span>
              </label>
              <div className="relative">
                <MapPin size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8FF00]" />
                <input
                  type="text" maxLength={200}
                  value={homeClub} onChange={(e) => setHomeClub(e.target.value)}
                  placeholder="vd: Sân Pickleball ABC, CLB XYZ…"
                  className="w-full pl-9 pr-3 h-11 rounded-xl bg-white/[0.05] border border-white/15 text-white text-[13.5px] font-semibold placeholder:text-white/30 focus:border-[#A8FF00]/60 focus:bg-white/[0.08] outline-none transition"
                  data-testid="self-declare-home-club-input"
                />
              </div>
              <select
                value={provinceCode}
                onChange={(e) => setProvinceCode(e.target.value)}
                className="w-full mt-2 h-10 px-3 rounded-xl bg-[#0F1623] border border-white/15 text-[12.5px] text-white focus:border-[#A8FF00]/60 outline-none transition appearance-none cursor-pointer"
                data-testid="self-declare-province-select"
              >
                <option value="">— Tỉnh/Thành phố —</option>
                {provinces.map((p) => (
                  <option key={p.code} value={p.code}>{p.name}</option>
                ))}
              </select>
            </div>

            {/* Note (optional) */}
            <div>
              <label className="block text-[11px] font-bold text-white/85 mb-1.5 tracking-wide">
                Ghi chú khác <span className="text-white/40 font-medium normal-case">(không bắt buộc)</span>
              </label>
              <textarea
                rows={2} maxLength={1000}
                value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Thông tin thêm bạn muốn BTC biết…"
                className="w-full px-3 py-2 rounded-xl bg-white/[0.05] border border-white/15 text-white text-[12.5px] placeholder:text-white/30 focus:border-[#A8FF00]/60 focus:bg-white/[0.08] outline-none transition resize-none"
                data-testid="self-declare-note-input"
              />
            </div>


            {/* Commitments */}
            <div className="space-y-2 pt-1">
              <label className="block text-[11px] font-bold text-white/85 mb-1 tracking-wide flex items-center gap-1.5">
                <ShieldWarning size={12} weight="fill" className="text-amber-400" />
                Cam kết của bạn <span className="text-rose-400">*</span>
              </label>

              <label
                className={`flex items-start gap-2.5 p-2.5 rounded-xl border cursor-pointer transition ${
                  confirmTruthful ? "bg-[#A8FF00]/[0.08] border-[#A8FF00]/40" : "bg-white/[0.03] border-white/12 hover:bg-white/[0.05]"
                }`}
                data-testid="self-declare-commit-truthful-label"
              >
                <input
                  type="checkbox" checked={confirmTruthful}
                  onChange={(e) => setConfirmTruthful(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[#A8FF00] shrink-0 cursor-pointer"
                  data-testid="self-declare-commit-truthful"
                />
                <span className="text-[11.5px] text-white/85 leading-snug">
                  Tôi xác nhận thông tin điểm trình đã cung cấp là <b className="text-white">chính xác và trung thực</b>.
                </span>
              </label>

              <label
                className={`flex items-start gap-2.5 p-2.5 rounded-xl border cursor-pointer transition ${
                  acceptLock ? "bg-amber-500/[0.08] border-amber-400/40" : "bg-white/[0.03] border-white/12 hover:bg-white/[0.05]"
                }`}
                data-testid="self-declare-commit-lock-label"
              >
                <input
                  type="checkbox" checked={acceptLock}
                  onChange={(e) => setAcceptLock(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-amber-400 shrink-0 cursor-pointer"
                  data-testid="self-declare-commit-lock"
                />
                <span className="text-[11.5px] text-white/85 leading-snug flex items-start gap-1">
                  <Lock size={11} weight="fill" className="mt-0.5 shrink-0 text-amber-400" />
                  <span>Tài khoản có thể bị <b className="text-amber-200">hạn chế đăng nhập</b> nếu khai báo không chính xác.</span>
                </span>
              </label>
            </div>

            {/* Submit */}
            <button
              type="button" onClick={submit} disabled={!canSubmit}
              className={`w-full h-12 rounded-xl font-black tracking-wider uppercase text-[13px] transition active:scale-[0.98] mt-2 ${
                canSubmit
                  ? "bg-gradient-to-r from-[#A8FF00] to-[#7FE600] text-[#0b1322] shadow-[0_6px_20px_-4px_rgba(168,255,0,0.5)] hover:shadow-[0_8px_24px_-4px_rgba(168,255,0,0.7)]"
                  : "bg-white/[0.05] text-white/35 cursor-not-allowed border border-white/10"
              }`}
              data-testid="self-declare-submit-btn"
            >
              {submitting ? "Đang gửi…" : "Gửi yêu cầu duyệt"}
            </button>

            <p className="text-[10px] text-white/45 leading-snug text-center pt-0.5">
              BTC sẽ kiểm tra trực tiếp trên nền tảng nguồn. Khai sai có thể bị từ chối + khoá tài khoản.
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
